import { useState, type ReactNode } from "react";
import { useSession } from "./SessionProvider";
import { useTurnstile } from "./useTurnstile";
import {
  requestStepUpGrant,
  requestStepUpGrantWithPassword,
  type StepUpAction,
  type StepUpResult
} from "./stepUp";

/**
 * Proving it is you, for an action whose step-up takes either the factor or a fresh password.
 *
 * `app/routes/MembershipPanel.tsx` carried this as a local hook and said on its own face that it was
 * a second copy of `useStepUpProof` in `app/routes/ManagementPanel.tsx`, and that the right end state
 * was one hook in `app/auth/`. Sending a secret became the third action with the same two branches on
 * 2026-09-13, which is the point where a third copy stops being a known duplication and starts being
 * the thing that drifts. So it lives here. `ManagementPanel` keeps its own copy for now and is named
 * in the round's report rather than changed in a round that is not about it.
 *
 * **The branch is the database's decision.** `public.request_step_up_grant` makes a caller who holds a
 * verified factor prove it with a fresh TOTP entry on an `aal2` session, and a caller with no factor
 * prove it with a `password` entry seconds old, which needs a temporary session and a Turnstile token
 * because Supabase applies CAPTCHA protection to `grant_type=password` itself. The hook's job is to
 * ask for the right one first, because finding out after six digits have been typed that the account
 * has no factor is worse than being asked for the password.
 *
 * The address comes from the session the person is already signed in with and never from a typed
 * field: architecture.md section 4 requires the temporary session's user id to equal the current
 * user, and an address taken from that same session is what makes the check meaningful.
 */

export type StepUpProofMode = "totp" | "password" | "factor-pending" | "factor-unknown" | "unknown-account";

export interface StepUpFactorState {
  id: string | null;
  unknown: boolean;
  pending: boolean;
}

export interface StepUpProof {
  mode: StepUpProofMode;
  ready: boolean;
  reset: () => void;
  request: (tenantId: string) => Promise<StepUpResult>;
}

export interface StepUpProofOptions {
  action: StepUpAction;
  factor: StepUpFactorState;
  disabled: boolean;
  /** Prefixes every id and test id, so two proofs can share a page and the existing selectors hold. */
  idPrefix: string;
  /** What the code or the password is for, said under the field. One sentence each. */
  totpReason: string;
  passwordReason: string;
}

export function useStepUpProof({
  action,
  factor,
  disabled,
  idPrefix,
  totpReason,
  passwordReason
}: StepUpProofOptions): [StepUpProof, ReactNode] {
  const { session, userId } = useSession();
  const [turnstile, turnstileMount] = useTurnstile();
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");

  const email = session?.user?.email ?? null;

  const mode: StepUpProofMode = factor.unknown
    ? "factor-unknown"
    : factor.pending
      ? "factor-pending"
      : factor.id
        ? "totp"
        : email && userId
          ? "password"
          : "unknown-account";

  const ready =
    mode === "totp"
      ? code.length >= 6
      : mode === "password"
        ? password.length > 0 && !turnstile.pending
        : false;

  function reset() {
    setCode("");
    setPassword("");
    turnstile.reset();
  }

  async function request(tenantId: string): Promise<StepUpResult> {
    if (mode === "totp" && factor.id) {
      return requestStepUpGrant({ factorId: factor.id, code, action, tenantId });
    }

    if (mode === "password" && email && userId) {
      return requestStepUpGrantWithPassword({
        email,
        password,
        userId,
        captchaToken: turnstile.token ?? undefined,
        action,
        tenantId
      });
    }

    return { ok: false, reason: "unavailable" };
  }

  const fields =
    mode === "factor-unknown" ? (
      <p className="form-error" role="alert" data-testid={`${idPrefix}-factor-unknown`}>
        Vi kunde inte läsa om ditt konto har en verifieringsapp. Ingenting har ändrats. Ladda om
        sidan och försök igen.
      </p>
    ) : mode === "factor-pending" ? (
      <p className="settings-note" data-testid={`${idPrefix}-factor-pending`}>
        Vi läser av ditt konto. Ett ögonblick.
      </p>
    ) : mode === "unknown-account" ? (
      <p className="form-error" role="alert" data-testid={`${idPrefix}-unknown-account`}>
        Vi kan inte se vilket konto du är inloggad med. Ingenting har ändrats. Ladda om sidan och
        försök igen.
      </p>
    ) : mode === "totp" ? (
      <div className="field">
        <label htmlFor={`${idPrefix}-code`}>Kod från din verifieringsapp</label>
        <input
          id={`${idPrefix}-code`}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          disabled={disabled}
          onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
        />
        <p className="settings-note">{totpReason}</p>
      </div>
    ) : (
      <>
        <div className="field">
          <label htmlFor={`${idPrefix}-password`}>Ditt nuvarande lösenord</label>
          <input
            id={`${idPrefix}-password`}
            type="password"
            autoComplete="current-password"
            value={password}
            disabled={disabled}
            onChange={(event) => setPassword(event.target.value)}
          />
          <p className="settings-note">{passwordReason}</p>
        </div>

        {/*
          architecture.md section 4 and the spike of 2026-08-30: a re-authentication without a
          Turnstile token is refused before the password is checked. With no sitekey configured
          there is no widget and nothing to wait for, which is the local stack.
        */}
        {turnstile.sitekey ? (
          <div ref={turnstileMount} className="cf-turnstile" data-testid={`turnstile-${idPrefix}`} />
        ) : null}
      </>
    );

  return [{ mode, ready, reset, request }, fields];
}
